// hooks/support/knowledge/useKnowledge.ts
import { useState, useCallback } from "react";
import { knowledgeService } from "../../../services/knowledgeService";
import { KnowledgeArticle } from "../../../types/features/KnowledgeArticle";

export default function useKnowledge() {
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeArticle | null>(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  const getAllArticles = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await knowledgeService.getAllArticles(page, perPage);
      setArticles(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
      return response;
    } catch (err) {
      setError("Erreur lors du chargement des articles");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const searchArticles = useCallback(async (query: string) => {
    if (!query.trim()) {
      setArticles([]);
      return;
    }

    setSearching(true);
    setError(null);
    try {
      const results = await knowledgeService.searchArticles(query);
      setArticles(results);
    } catch (err) {
      setError("Erreur lors de la recherche");
      console.error(err);
    } finally {
      setSearching(false);
    }
  }, []);

  const getArticle = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const article = await knowledgeService.getArticleById(id);
      setSelectedArticle(article);
      return article;
    } catch (err) {
      setError("Erreur lors du chargement de l'article");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createArticle = useCallback(async (data: Omit<KnowledgeArticle, "id" | "created_at" | "updated_at">) => {
    setSaving(true);
    setError(null);
    try {
      const newArticle = await knowledgeService.createArticle(data);
      setArticles((prev) => [newArticle, ...prev]);
      return newArticle;
    } catch (err) {
      setError("Erreur lors de la création");
      console.error(err);
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const updateArticle = useCallback(async (id: string, data: Partial<KnowledgeArticle>) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await knowledgeService.updateArticle(id, data);
      setArticles((prev) => prev.map((a) => (a.id === id ? updated : a)));
      return updated;
    } catch (err) {
      setError("Erreur lors de la mise à jour");
      console.error(err);
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const deleteArticle = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await knowledgeService.deleteArticle(id);
      setArticles((prev) => prev.filter((a) => a.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setArticles([]);
    setSelectedArticle(null);
  }, []);

  return {
    articles,
    selectedArticle,
    loading,
    searching,
    saving,
    error,
    pagination,
    getAllArticles,
    searchArticles,
    getArticle,
    createArticle,
    updateArticle,
    deleteArticle,
    clearResults,
  };
}
